import { useState, useRef, useCallback, useEffect } from "react";
import PageSEO from "@/components/PageSEO";
import { motion, useInView, useScroll, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Zap, MapPin, Calculator, FileText, ChevronRight, Sun, TrendingUp, Shield,
  Home, Building2, HardHat, Briefcase, Cpu, Check, Play, ArrowRight,
  ChevronLeft, Menu, X, IndianRupee, Clock, Sparkles, Star, Leaf,
} from "lucide-react";

// ─── Palette tokens ───────────────────────────────────────────────────────────
const F = {
  // Dark brown scale (dark sections, headers, text)
  950: "#2d2219", 900: "#3a2e28", 800: "#504136", 700: "#5e4e41",
  600: "#6e5c4e",
  // Teal primary → bright mint highlight
  500: "#689689", 400: "#83E8BA", 300: "#8ab0a8",
  200: "#B2E6D4", 100: "#c8ede4", 50: "#edf8f4",
  // Taupe accent
  e500: "#A49E8D", e400: "#8c8678", e300: "#c0b9ac", e100: "#f0ede8",
  // Teal text/muted variants
  m600: "#2d4f4b", m500: "#3d6b65", m400: "#4a7068", m300: "#5e8480",
  // Light taupe backgrounds
  c100: "#f5f2ec", c200: "#e8e3d8", c50: "#faf9f6",
};

// ─── Animation helpers ────────────────────────────────────────────────────────
function Reveal({ children, className, delay = 0, y = 40, ...props }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-70px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

// ─── Static data ──────────────────────────────────────────────────────────────
// Product images shared with ChargerCatalog
const CHARGER_IMAGES = {
  "ABB":               "/chargers/abb.jpg",
  "Delta Electronics": "/chargers/delta.jpg",
  "Exicom":            "/chargers/exicom.jpg",
  "Greaves Electric":  "/chargers/greaves.jpg",
  "Okaya EV":          "/chargers/okaya.jpg",
  "Servotech":         "/chargers/servotech.png",
  "Siemens":           "/chargers/siemens.png",
  "Tata Power EV":     "/chargers/tata.png",
};

// Per-brand image tuning (bg color + object-position)
const CHARGER_IMG_CFG = {
  "ABB":               { bg: "#f5f5f5", pos: "center center" },
  "Delta Electronics": { bg: "#f5f5f5", pos: "center center" },
  "Exicom":            { bg: "#e8eef2", pos: "center 35%" },
  "Greaves Electric":  { bg: "#0d0e10", pos: "center center" },
  "Okaya EV":          { bg: "#f5f5f5", pos: "72% center" },
  "Servotech":         { bg: "#f5f5f5", pos: "center 8%" },
  "Siemens":           { bg: "#f5f5f5", pos: "center center" },
  "Tata Power EV":     { bg: "#d4e8c2", pos: "center center" },
};

// Brand gradient themes for cards without a photo
const CHARGER_THEMES = {
  "Tata Power EV":    { from: "#1e3a5f", to: "#0f7dc2", accent: "#38bdf8" },
  "Jio-BP":           { from: "#1e293b", to: "#0ea5e9", accent: "#7dd3fc" },
  "ChargePoint":      { from: "#1a1a1a", to: "#f97316", accent: "#fb923c" },
  "Magenta Power":    { from: "#4a004a", to: "#d946ef", accent: "#f0abfc" },
  "Kazam EV":         { from: "#082f49", to: "#0284c7", accent: "#7dd3fc" },
};

const CHARGERS = [
  { brand: "Tata Power EV",    model: "EZ Station 50",    power: "50 kW", type: "DC", price: "₹7.2L",  tag: "Commercial", fame: true  },
  { brand: "Exicom",           model: "FE-30 Plus",       power: "30 kW", type: "DC", price: "₹3.5L",  tag: "Fleet",       fame: true  },
  { brand: "Delta Electronics",model: "DC Wallbox 25",    power: "25 kW", type: "DC", price: "₹2.8L",  tag: "Fleet",       fame: false },
  { brand: "ABB",              model: "Terra 54 HV",      power: "54 kW", type: "DC", price: "₹9.5L",  tag: "Highway",     fame: true  },
  { brand: "Servotech",        model: "ST-60 Pro",        power: "60 kW", type: "DC", price: "₹6.5L",  tag: "Commercial", fame: true  },
  { brand: "Jio-BP",           model: "Pulse 60",         power: "60 kW", type: "DC", price: "₹7L",    tag: "Highway",     fame: false },
  { brand: "ChargePoint",      model: "CP6000",           power: "62 kW", type: "DC", price: "₹10L",   tag: "Premium",     fame: false },
  { brand: "Magenta Power",    model: "ChargeGrid AC22",  power: "22 kW", type: "AC", price: "₹1.2L",  tag: "Workplace",   fame: true  },
  { brand: "Greaves Electric", model: "Ampere Pro 15",    power: "15 kW", type: "AC", price: "₹85K",   tag: "Retail",      fame: false },
  { brand: "Okaya EV",         model: "OK-DC-30",         power: "30 kW", type: "DC", price: "₹3.8L",  tag: "Commercial", fame: true  },
  { brand: "Kazam EV",         model: "K7 AC Charger",   power: "7.4 kW",type: "AC", price: "₹42K",   tag: "Home",        fame: false },
  { brand: "Siemens",          model: "SICHARGE D75",     power: "75 kW", type: "DC", price: "₹14L",   tag: "Enterprise",  fame: false },
];

const FEATURES = [
  { icon: MapPin,        title: "AI Site Classification",    color: F[500],  desc: "Drop in your land dimensions and state. Our AI classifies the site and surfaces opportunities in seconds.", bullets: ["Urban, highway & semi-urban analysis", "Constraints & opportunities flagged", "State-specific tariff lookup"] },
  { icon: Zap,           title: "Optimised Layout Design",   color: F.e400,  desc: "Deterministic algorithm places chargers, parking, buildings, and solar panels for maximum coverage.", bullets: ["Canvas-based drag-and-drop editor", "Automatic area coverage calculation", "Multi-shape land support"] },
  { icon: Calculator,    title: "Transparent CAPEX Model",   color: F[400],  desc: "Every cost head itemised — civil, electrical, solar, buildings, chargers, and miscellaneous.", bullets: ["Real-time totals as you change inputs", "State-adjusted cost multipliers", "Per-charger cost breakdown"] },
  { icon: TrendingUp,    title: "ROI & Scenario Analysis",   color: F.e500,  desc: "Interactive sliders for utilisation, revenue per session, and CAPEX multipliers. See payback and IRR update live.", bullets: ["10-year revenue projection", "Payback period & IRR", "Side-by-side scenario comparison"] },
  { icon: Shield,        title: "Compliance Checklist",      color: F.m400,  desc: "India-specific compliance across safety, electrical, fire, and environmental categories.", bullets: ["Central + state regulations merged", "Mandatory vs recommended flagged", "Progressive tick-off as you build"] },
  { icon: IndianRupee,   title: "Government Subsidy Finder", color: F[300],  desc: "FAME III, PM e-DRIVE, state schemes and MSME financing — matched to your project automatically.", bullets: ["29 states covered", "Total subsidy & net CAPEX shown", "Application links included"] },
];

const AUDIENCES = [
  { icon: Home,      role: "Landowners",       tagline: "Is my land right for EV charging?",      bullets: ["Check land suitability", "Model lease vs ownership revenue", "Understand permits needed"] },
  { icon: Building2, role: "Infra Developers", tagline: "Can I build a viable business case?",     bullets: ["Full CAPEX breakdown", "ROI & payback projections", "Investor-ready feasibility reports"] },
  { icon: HardHat,   role: "EPC Contractors",  tagline: "What do I need to scope this project?",   bullets: ["Optimised layouts & placement", "Itemised civil & electrical costs", "Compliance flags"] },
  { icon: Briefcase, role: "Consultants",       tagline: "How do I deliver faster to clients?",     bullets: ["What-if scenario sliders", "Branded PDF in one click", "Reusable project templates"] },
  { icon: Cpu,       role: "OEMs",              tagline: "Does this site fit my products?",          bullets: ["Charger type recommendations", "Grid load & solar sizing", "Pre-sales engineering data"] },
];

const STEPS = [
  { step: 1, title: "Enter Site Details",     detail: "Define your location, state, land dimensions, shape, and intended use-case. The platform auto-populates state tariffs and government incentives." },
  { step: 2, title: "AI Generates Layout",    detail: "Our AI classifies your site, recommends charger types, and generates an optimised layout — in under 30 seconds." },
  { step: 3, title: "Review & Customise",     detail: "Use the interactive canvas to adjust positions, tweak the ROI sliders, and model different scenarios until the numbers work." },
  { step: 4, title: "Export Report",          detail: "One click generates a polished PDF with layout, CAPEX breakdown, ROI projections, and compliance checklist — ready to share with investors." },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionLabel({ text, color = F[500] }) {
  return (
    <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color }}>
      {text}
    </p>
  );
}

function ChargerCard({ charger }) {
  const imgSrc = CHARGER_IMAGES[charger.brand];
  const imgCfg = CHARGER_IMG_CFG[charger.brand] || { bg: "#f5f5f5", pos: "center center" };
  const theme  = CHARGER_THEMES[charger.brand];

  return (
    <div
      className="flex-none rounded-2xl overflow-hidden border transition-all duration-300 hover:-translate-y-1"
      style={{ minWidth: 220, width: 248, background: "#fff", borderColor: F[100] }}
    >
      {/* ── Image / gradient hero ── */}
      <div className="relative" style={{ height: 156 }}>
        {imgSrc ? (
          <>
            <div className="absolute inset-0" style={{ background: imgCfg.bg }} />
            <img
              src={imgSrc}
              alt={charger.model}
              className="absolute inset-0 w-full h-full"
              style={{ objectFit: "contain", objectPosition: imgCfg.pos, padding: "10px" }}
            />
          </>
        ) : (
          <div className="absolute inset-0" style={{
            background: theme
              ? `linear-gradient(135deg, ${theme.from} 0%, ${theme.to} 100%)`
              : `linear-gradient(135deg, ${F[700]} 0%, ${F[400]} 100%)`
          }}>
            <div className="absolute inset-0 flex items-center justify-center opacity-20">
              <span className="text-white text-6xl font-black font-mono">{charger.power.split(" ")[0]}</span>
            </div>
          </div>
        )}
        {/* type badge */}
        <span className="absolute top-2.5 left-2.5 text-xs px-2 py-0.5 rounded-full font-semibold"
          style={charger.type === "DC"
            ? { background: "#0f172acc", color: "#7dd3fc" }
            : { background: "#0f172acc", color: "#86efac" }}>
          {charger.type}
        </span>
        {charger.fame && (
          <span className="absolute top-2.5 right-2.5 text-xs px-2 py-0.5 rounded-full font-semibold"
            style={{ background: "#0f172acc", color: "#fde68a" }}>
            FAME ✓
          </span>
        )}
      </div>

      {/* ── Info ── */}
      <div className="p-4">
        <p className="text-xs font-medium mb-0.5" style={{ color: F.m500 }}>{charger.brand}</p>
        <p className="font-semibold text-sm mb-2" style={{ color: F[900] }}>{charger.model}</p>
        <p className="text-3xl font-black font-mono leading-none mb-3" style={{ color: F[700] }}>
          {charger.power}
        </p>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs px-2 py-0.5 rounded-full border"
            style={{ background: `${F.e500}10`, color: F.e400, borderColor: `${F.e500}20` }}>
            {charger.tag}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-base font-bold" style={{ color: F[500] }}>{charger.price}</p>
          <span className="text-xs" style={{ color: F.m500 }}>est. price</span>
        </div>
      </div>
    </div>
  );
}

function PromoVideo({ label = "Product Demo", src = "/videos/promo.mp4" }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  return (
    <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl"
      style={{ aspectRatio: "16/9", background: F[900] }}>
      <video
        ref={videoRef}
        src={src}
        className="w-full h-full object-cover"
        loop
        muted={muted}
        playsInline
        autoPlay
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      {/* Controls overlay — visible on hover */}
      <div className="absolute inset-0 flex items-end justify-between p-4 opacity-0 hover:opacity-100 transition-opacity"
        style={{ background: "linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 60%)" }}>
        <button onClick={toggle}
          className="w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-sm transition-transform hover:scale-110"
          style={{ background: `${F.c100}22`, border: `1px solid ${F.c100}44` }}>
          {playing
            ? <span className="w-3 h-3 flex gap-1"><span className="w-1 h-full rounded-sm" style={{ background: F.c100 }} /><span className="w-1 h-full rounded-sm" style={{ background: F.c100 }} /></span>
            : <Play className="w-4 h-4 ml-0.5" style={{ color: F.c100 }} fill={F.c100} />}
        </button>
        <button onClick={() => { setMuted(m => !m); if (videoRef.current) videoRef.current.muted = !muted; }}
          className="text-xs px-3 py-1.5 rounded-full backdrop-blur-sm transition"
          style={{ background: `${F.c100}22`, color: F.c100, border: `1px solid ${F.c100}44` }}>
          {muted ? "🔇 Unmute" : "🔊 Mute"}
        </button>
      </div>
    </div>
  );
}

function SliderRow({ label, value, min, max, step = 1, onChange, format }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <label className="text-sm" style={{ color: F.m400 }}>{label}</label>
        <span className="text-sm font-semibold font-mono" style={{ color: F[800] }}>{format(value)}</span>
      </div>
      <div className="relative h-2 rounded-full" style={{ background: F[100] }}>
        <div className="absolute h-full rounded-full transition-all" style={{ width: `${pct}%`, background: F[500] }} />
        <input type="range" min={min} max={max} step={step} value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-xs" style={{ color: F.m400 }}>{format(min)}</span>
        <span className="text-xs" style={{ color: F.m400 }}>{format(max)}</span>
      </div>
    </div>
  );
}

function ROIWidget({ onCTA }) {
  const [chargerCount, setChargerCount] = useState(6);
  const [sessionsPerDay, setSessionsPerDay] = useState(12);
  const [revenuePerSession, setRevenuePerSession] = useState(150);

  const monthlyRevenue = chargerCount * sessionsPerDay * revenuePerSession * 30;
  const capex = chargerCount * 750000;
  const annualRevenue = monthlyRevenue * 12;
  const paybackYears = annualRevenue > 0 ? (capex / annualRevenue).toFixed(1) : "—";

  const fmt = (n) => {
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
    if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
    return `₹${(n / 1000).toFixed(0)}K`;
  };

  return (
    <div className="rounded-2xl p-8 border" style={{ background: F.c50, borderColor: F[100] }}>
      <div className="grid lg:grid-cols-2 gap-10 items-start">
        <div className="space-y-8">
          <SliderRow label="Number of DC chargers" value={chargerCount} min={2} max={20} onChange={setChargerCount} format={(v) => `${v} chargers`} />
          <SliderRow label="Sessions per charger / day" value={sessionsPerDay} min={3} max={30} onChange={setSessionsPerDay} format={(v) => `${v} sessions`} />
          <SliderRow label="Revenue per session" value={revenuePerSession} min={50} max={300} step={10} onChange={setRevenuePerSession} format={(v) => `₹${v}`} />
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "Monthly Revenue", value: fmt(monthlyRevenue), color: F[500] },
              { label: "Annual Revenue",  value: fmt(annualRevenue),  color: F[500] },
              { label: "Est. CAPEX",      value: fmt(capex),          color: F[800] },
              { label: "Payback Period",  value: `${paybackYears} yrs`, color: F.e500 },
            ].map((item, i) => (
              <div key={i} className="rounded-xl p-4 border" style={{ background: "#fff", borderColor: F[100] }}>
                <p className="text-xs mb-1" style={{ color: F.m600 }}>{item.label}</p>
                <p className="text-2xl font-black font-mono" style={{ color: item.color }}>{item.value}</p>
              </div>
            ))}
          </div>
          <p className="text-xs" style={{ color: F.m500 }}>Rough estimate. Sign up for detailed AI analysis with subsidies, compliance & hardware matching.</p>
          <button onClick={onCTA} className="w-full py-3 rounded-xl font-semibold text-sm transition-all hover:opacity-90 flex items-center justify-center gap-2"
            style={{ background: F[500], color: F.c50 }}>
            Get Detailed Analysis Free <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Navbar ───────────────────────────────────────────────────────────────────
function Navbar({ onLogin, user }) {
  const { scrollY } = useScroll();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = scrollY.on("change", (v) => setScrolled(v > 20));
    return unsub;
  }, [scrollY]);

  const navLinks = [
    { label: "Features", href: "#features" },
    { label: "Catalog",  href: "#catalog"  },
    { label: "ROI Calc", href: "#roi"      },
    { label: "Pricing",  href: "#pricing"  },
    { label: "Blog",     href: "/blog", external: true },
  ];
  const scrollTo = (id) => { document.querySelector(id)?.scrollIntoView({ behavior: "smooth" }); setMobileOpen(false); };

  return (
    <motion.header
      className="sticky top-0 z-50 transition-all duration-300"
      style={scrolled ? { background: `${F.c100}f0`, backdropFilter: "blur(20px)", borderBottom: `1px solid ${F[200]}`, boxShadow: `0 4px 24px ${F[200]}80` } : { background: "transparent" }}
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shadow-md" style={{ background: F[500] }}>
              <Leaf className="w-5 h-5" style={{ color: F.c50 }} />
            </div>
            <span className="font-black text-lg tracking-tight" style={{ fontFamily: "Chivo, sans-serif", color: F[900] }}>
              EV Hub Studio
            </span>
          </div>

          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) =>
              link.external ? (
                <button key={link.label} onClick={() => navigate(link.href)}
                  className="px-4 py-2 text-sm rounded-lg transition-all"
                  style={{ color: F.m600 }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = F[800]; e.currentTarget.style.background = `${F[500]}10`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = F.m600; e.currentTarget.style.background = "transparent"; }}>
                  {link.label}
                </button>
              ) : (
                <button key={link.label} onClick={() => scrollTo(link.href)}
                  className="px-4 py-2 text-sm rounded-lg transition-all"
                  style={{ color: F.m600 }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = F[800]; e.currentTarget.style.background = `${F[500]}10`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = F.m600; e.currentTarget.style.background = "transparent"; }}>
                  {link.label}
                </button>
              )
            )}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <button onClick={() => navigate("/dashboard")} className="px-5 py-2 rounded-lg text-sm font-semibold flex items-center gap-1 transition-all hover:opacity-90"
                style={{ background: F[500], color: F.c50 }}>
                Dashboard <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <>
                <button onClick={onLogin} className="px-4 py-2 text-sm font-medium transition-all" style={{ color: F.m600 }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = F[800])}
                  onMouseLeave={(e) => (e.currentTarget.style.color = F.m600)}>
                  Sign In
                </button>
                <button onClick={onLogin} className="px-5 py-2 rounded-lg text-sm font-semibold transition-all hover:opacity-90 shadow-md"
                  style={{ background: F.e500, color: F.c50 }}>
                  Get Started Free
                </button>
              </>
            )}
          </div>

          <button className="md:hidden p-2 transition-colors" style={{ color: F.m600 }} onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.25 }}
            style={{ background: F.c100, borderBottom: `1px solid ${F[200]}` }} className="md:hidden overflow-hidden">
            <div className="container mx-auto px-4 py-4 space-y-1">
              {navLinks.map((link) => (
                <button key={link.label} onClick={() => { link.external ? navigate(link.href) : scrollTo(link.href); setMobileOpen(false); }}
                  className="block w-full text-left px-4 py-3 rounded-lg transition-all text-sm"
                  style={{ color: F.m600 }}>
                  {link.label}
                </button>
              ))}
              <div className="pt-3 flex flex-col gap-2" style={{ borderTop: `1px solid ${F[200]}` }}>
                <button onClick={onLogin} className="w-full py-3 rounded-lg text-sm border transition-all" style={{ borderColor: F[300], color: F[700] }}>Sign In</button>
                <button onClick={onLogin} className="w-full py-3 rounded-lg text-sm font-semibold" style={{ background: F[500], color: F.c50 }}>Get Started Free</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
const LandingPage = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [activeAudience, setActiveAudience] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const carouselRef = useRef(null);
  const scrollPrev = useCallback(() => carouselRef.current?.scrollBy({ left: -280, behavior: "smooth" }), []);
  const scrollNext = useCallback(() => carouselRef.current?.scrollBy({ left: 280, behavior: "smooth" }), []);
  const handleCTA = () => login();
  const heroWords = ["Plan", "Your", "EV", "Charging", "Station", "in", "Minutes."];

  // Pre-warm the backend so Sign In is instant (prevents Render cold start delay)
  useEffect(() => {
    fetch(`${API}/health`, { method: "GET" }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: F.c100 }}>
      <PageSEO
        title="EV Hub Studio — EV Charging Station Design, CAPEX & ROI Planning for India"
        description="AI-powered platform to design EV charging stations, model CAPEX and ROI, select sites, compare AC/DC chargers and find government subsidies — built for India."
        path="/"
      />
      <Navbar onLogin={login} user={user} />

      {/* ── HERO (warm cream — like "Try It Free" section) ───────── */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden" style={{ background: F.c200 }}>
        {/* Subtle soft glows */}
        <div className="absolute top-10 left-1/3 w-[500px] h-[500px] rounded-full blur-[140px] pointer-events-none" style={{ background: `${F[400]}18` }} />
        <div className="absolute bottom-0 right-1/4 w-[350px] h-[350px] rounded-full blur-[120px] pointer-events-none" style={{ background: `${F.e500}12` }} />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-8 border"
                style={{ background: `${F[500]}15`, borderColor: `${F[500]}30`, color: F[600] }}>
                <Sparkles className="w-3.5 h-3.5" />
                Free for all users during launch month
              </motion.div>

              <motion.h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.05] mb-6"
                style={{ fontFamily: "Chivo, sans-serif" }}
                variants={{ hidden: {}, show: { transition: { staggerChildren: 0.07 } } }}
                initial="hidden" animate="show">
                {heroWords.map((word, i) => (
                  <motion.span key={i} variants={{ hidden: { opacity: 0, y: 40 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } } }}
                    className="inline-block mr-4"
                    style={{ color: (word === "EV" || word === "Station") ? F[500] : F[900] }}>
                    {word}
                  </motion.span>
                ))}
              </motion.h1>

              <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.55 }}
                className="text-lg mb-10 max-w-xl leading-relaxed" style={{ color: F.m600 }}>
                AI-powered site layout, transparent CAPEX modeling, ROI projections, and India compliance checklists — built for the EV infrastructure boom.
              </motion.p>

              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.68 }}
                className="flex flex-col sm:flex-row gap-4 mb-6">
                <button onClick={() => navigate("/try")}
                  className="px-8 py-4 rounded-xl font-semibold text-base flex items-center gap-2 justify-center transition-all hover:opacity-90 shadow-lg"
                  style={{ background: F.e500, color: F.c50, boxShadow: `0 8px 28px ${F.e500}30` }}>
                  Try Designer Free — No Sign In
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button onClick={() => document.getElementById("video")?.scrollIntoView({ behavior: "smooth" })}
                  className="px-8 py-4 rounded-xl font-semibold text-base flex items-center gap-2 justify-center transition-all border"
                  style={{ borderColor: F[300], color: F[700], background: "transparent" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = `${F[500]}10`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                  <Play className="w-4 h-4" /> Watch Demo
                </button>
              </motion.div>

              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.85 }}
                className="text-sm" style={{ color: F.m500 }}>
                No credit card &nbsp;·&nbsp; No account to try &nbsp;·&nbsp; 29 states covered
              </motion.p>
            </div>

            <motion.div className="relative" initial={{ opacity: 0, x: 60 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}>
              <div className="relative">
                <div className="rounded-2xl p-2 border shadow-2xl overflow-hidden" style={{ background: "#fff", borderColor: F[100] }}>
                  <video
                    src="/videos/promo.mp4"
                    className="rounded-xl w-full block"
                    autoPlay
                    muted
                    loop
                    playsInline
                  />
                </div>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
                  className="absolute -bottom-6 -left-8 rounded-xl p-4 border shadow-xl"
                  style={{ background: "#fff", borderColor: F[100] }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${F[500]}15` }}>
                      <TrendingUp className="w-5 h-5" style={{ color: F[500] }} />
                    </div>
                    <div>
                      <p className="text-xs" style={{ color: F.m500 }}>Avg. ROI</p>
                      <p className="text-xl font-black font-mono" style={{ color: F[800] }}>18–25%</p>
                    </div>
                  </div>
                </motion.div>
                <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.1 }}
                  className="absolute -top-4 -right-4 rounded-xl p-3 border shadow-xl"
                  style={{ background: "#fff", borderColor: F[100] }}>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${F.e500}15` }}>
                      <Clock className="w-4 h-4" style={{ color: F.e500 }} />
                    </div>
                    <div>
                      <p className="text-xs" style={{ color: F.m500 }}>Layout in</p>
                      <p className="text-base font-black" style={{ color: F[800] }}>&lt; 30s</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── STATS (medium forest strip) ──────────────────────────── */}
      <Reveal style={{ background: F[700], borderTop: `1px solid ${F[600]}`, borderBottom: `1px solid ${F[600]}` }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4" style={{ borderLeft: `1px solid ${F[600]}` }}>
            {[
              { value: "12+",    label: "OEM Brands",        color: F[200]  },
              { value: "18–25%", label: "Avg. ROI",          color: F.e300  },
              { value: "29+",    label: "States Covered",    color: F.m300  },
              { value: "< 30s",  label: "Layout Generation", color: F.e400  },
            ].map((stat, i) => (
              <div key={i} className="py-7 px-6 text-center" style={{ borderRight: `1px solid ${F[600]}` }}>
                <p className="text-3xl font-black font-mono" style={{ color: stat.color }}>{stat.value}</p>
                <p className="text-sm mt-1" style={{ color: F.m300 }}>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      {/* ── VIDEO (warm cream) ───────────────────────────────────── */}
      <section id="video" className="py-24" style={{ background: F.c100 }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <SectionLabel text="See It In Action" color={F[500]} />
            <h2 className="text-4xl sm:text-5xl font-black mb-4" style={{ fontFamily: "Chivo, sans-serif", color: F[900] }}>
              From Site Details to<br />
              <span style={{ color: F[500] }}>Investor Report in 2 Minutes</span>
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: F.m600 }}>
              Watch how EV Hub Studio turns a plot of land into a fully costed, compliant EV charging station plan.
            </p>
          </Reveal>
          <Reveal delay={0.2}><PromoVideo label="EV Hub Studio — Full Walkthrough" src="https://pub-13b3add21173495596ccae87f6483e34.r2.dev/tutorial.mp4" /></Reveal>
        </div>
      </section>

      {/* ── CATALOG (light sage) ─────────────────────────────────── */}
      <section id="catalog" className="py-24" style={{ background: F[50] }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 gap-4">
            <div>
              <SectionLabel text="Hardware Catalog" color={F.e500} />
              <h2 className="text-4xl sm:text-5xl font-black" style={{ fontFamily: "Chivo, sans-serif", color: F[900] }}>
                India's Top EV Charger OEMs
                <span className="block text-3xl font-normal mt-1" style={{ color: F.m600 }}>All in One Platform</span>
              </h2>
            </div>
            <button onClick={() => navigate("/charger-catalog")}
              className="px-5 py-2.5 rounded-xl text-sm font-medium border transition-all hover:border-forest-500 flex-shrink-0"
              style={{ borderColor: F[200], color: F[700] }}>
              View Full Catalog <ChevronRight className="inline w-4 h-4 ml-1" />
            </button>
          </Reveal>
          <Reveal delay={0.15}>
            <div ref={carouselRef} className="flex gap-4 pb-2 overflow-x-auto scroll-smooth" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
              {CHARGERS.map((charger, i) => <ChargerCard key={i} charger={charger} />)}
            </div>
            <div className="flex items-center gap-2 mt-5">
              <button onClick={scrollPrev} className="w-9 h-9 rounded-full border flex items-center justify-center transition-all"
                style={{ borderColor: F[200], color: F.m600 }}>
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={scrollNext} className="w-9 h-9 rounded-full border flex items-center justify-center transition-all"
                style={{ borderColor: F[200], color: F.m600 }}>
                <ChevronRight className="w-4 h-4" />
              </button>
              <p className="text-sm ml-2" style={{ color: F.m500 }}>Drag to browse · {CHARGERS.length} chargers across 12 OEMs</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── TRY IT FREE (warm cream) ─────────────────────────────── */}
      <section className="py-24" style={{ background: F.c200 }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <Reveal>
              <SectionLabel text="No Account Needed" color={F[500]} />
              <h2 className="text-4xl sm:text-5xl font-black mb-6" style={{ fontFamily: "Chivo, sans-serif", color: F[900] }}>
                Design Your EV Hub<br />
                <span style={{ color: F[500] }}>Right Now</span>
              </h2>
              <p className="text-lg mb-8 leading-relaxed" style={{ color: F.m600 }}>
                Enter your site details and get an optimised layout with cost estimates — no sign-up required to get started.
              </p>
              <div className="space-y-3 mb-10">
                {[
                  { text: "Site layout & charger placement",  free: true  },
                  { text: "Basic CAPEX cost estimate",        free: true  },
                  { text: "Charger recommendations",          free: true  },
                  { text: "Full ROI & payback analysis",      free: false },
                  { text: "Government subsidy finder",        free: false },
                  { text: "PDF report export",                free: false },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                      style={item.free ? { background: `${F[500]}18` } : { background: F[100] }}>
                      {item.free ? <Check className="w-3 h-3" style={{ color: F[500] }} /> : <span className="text-xs">🔒</span>}
                    </div>
                    <span style={{ color: item.free ? F[800] : F.m500 }}>{item.text}</span>
                    {!item.free && (
                      <span className="text-xs px-2 py-0.5 rounded-full border"
                        style={{ background: `${F[500]}10`, color: F[500], borderColor: `${F[500]}25` }}>
                        Free with account
                      </span>
                    )}
                  </div>
                ))}
              </div>
              <button onClick={() => navigate("/try")}
                className="px-8 py-4 rounded-xl font-semibold text-base flex items-center gap-2 transition-all hover:opacity-90 shadow-lg"
                style={{ background: F[500], color: F.c50, boxShadow: `0 8px 24px ${F[500]}30` }}>
                Start Designing — It's Free <ArrowRight className="w-5 h-5" />
              </button>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="relative">
                <div className="rounded-2xl p-2 border shadow-2xl" style={{ background: "#fff", borderColor: F[100] }}>
                  <img src="https://customer-assets.emergentagent.com/job_f60131a8-3ce8-4679-b439-ffd532e34521/artifacts/o5ipnsdl_EV_Hub%20sketch.png"
                    alt="EV Hub Studio interface" className="rounded-xl w-full" />
                </div>
                <div className="absolute -bottom-5 -right-5 rounded-xl p-4 shadow-xl"
                  style={{ background: F[500] }}>
                  <p className="text-xs font-medium" style={{ color: F[100] }}>Generated in</p>
                  <p className="text-xl font-black" style={{ color: "#fff" }}>28 sec</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── ROI CALCULATOR (dark forest) ─────────────────────────── */}
      <section id="roi" className="py-24" style={{ background: F[900] }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <SectionLabel text="ROI Calculator" color={F[300]} />
            <h2 className="text-4xl sm:text-5xl font-black mb-4" style={{ fontFamily: "Chivo, sans-serif", color: F.c100 }}>
              How Much Can Your<br />
              <span style={{ color: F[300] }}>EV Station Earn?</span>
            </h2>
            <p className="text-lg max-w-xl mx-auto" style={{ color: F.m400 }}>
              Adjust the sliders for a quick estimate. Sign up for a full AI-powered analysis with subsidies, hardware, and compliance.
            </p>
          </Reveal>
          <Reveal delay={0.2}><ROIWidget onCTA={handleCTA} /></Reveal>
        </div>
      </section>

      {/* ── FEATURES (light sage) ────────────────────────────────── */}
      <section id="features" className="py-24" style={{ background: F[50] }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-14">
            <SectionLabel text="Everything You Need" color={F.e500} />
            <h2 className="text-4xl sm:text-5xl font-black mb-4" style={{ fontFamily: "Chivo, sans-serif", color: F[900] }}>
              One Platform, Complete<br />EV Station Planning
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: F.m600 }}>
              From site analysis to investor-ready PDF reports — no more juggling spreadsheets, consultants, and compliance docs.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <Reveal key={i} delay={i * 0.08}
                  className="rounded-2xl p-6 border transition-all duration-300 hover:-translate-y-1"
                  style={{ background: "#fff", borderColor: F[100] }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ background: `${f.color}18` }}>
                    <Icon className="w-6 h-6" style={{ color: f.color }} />
                  </div>
                  <h3 className="font-bold text-lg mb-2" style={{ color: F[900] }}>{f.title}</h3>
                  <p className="text-sm mb-4 leading-relaxed" style={{ color: F.m600 }}>{f.desc}</p>
                  <ul className="space-y-2">
                    {f.bullets.map((b, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <Check className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: F[400] }} />
                        <span className="text-xs" style={{ color: F.m600 }}>{b}</span>
                      </li>
                    ))}
                  </ul>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── WHO IT'S FOR (warm cream) ────────────────────────────── */}
      <section className="py-24" style={{ background: F.c100 }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <SectionLabel text="Built For You" color={F[500]} />
            <h2 className="text-4xl sm:text-5xl font-black mb-4" style={{ fontFamily: "Chivo, sans-serif", color: F[900] }}>
              Who Uses EV Hub Studio?
            </h2>
            <p className="text-lg max-w-xl mx-auto" style={{ color: F.m600 }}>Select your role to see what the platform gives you.</p>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            {AUDIENCES.map((a, i) => {
              const Icon = a.icon;
              const isActive = activeAudience === i;
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <button onClick={() => setActiveAudience(isActive ? null : i)}
                    className="w-full rounded-2xl border-2 p-5 flex flex-col items-center text-center transition-all duration-200"
                    style={isActive
                      ? { borderColor: F[500], background: `${F[500]}0a` }
                      : { borderColor: F[100], background: "#fff" }}>
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                      style={{ background: `${F[500]}15`, transform: isActive ? "scale(1.1)" : "scale(1)", transition: "transform 0.2s" }}>
                      <Icon className="w-6 h-6" style={{ color: F[500] }} />
                    </div>
                    <h3 className="font-semibold text-sm mb-1" style={{ color: F[900] }}>{a.role}</h3>
                    <p className="text-xs italic leading-tight" style={{ color: F.m600 }}>{a.tagline}</p>
                  </button>
                </Reveal>
              );
            })}
          </div>
          <AnimatePresence mode="wait">
            {activeAudience !== null && (
              <motion.div key={activeAudience} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}
                className="rounded-2xl border p-8" style={{ background: "#fff", borderColor: F[100] }}>
                <div className="grid sm:grid-cols-3 gap-4">
                  {AUDIENCES[activeAudience].bullets.map((b, i) => (
                    <div key={i} className="flex items-start gap-3 rounded-xl border px-4 py-3" style={{ borderColor: F[100] }}>
                      <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: F[400] }} />
                      <span className="text-sm" style={{ color: F[800] }}>{b}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* ── HOW IT WORKS (dark forest) ───────────────────────────── */}
      <section className="py-24" style={{ background: F[800] }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-14">
            <SectionLabel text="Simple Process" color={F.e300} />
            <h2 className="text-4xl sm:text-5xl font-black mb-4" style={{ fontFamily: "Chivo, sans-serif", color: F.c100 }}>
              Four Steps to an<br />
              <span style={{ color: F.e300 }}>Investor-Ready Report</span>
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6 relative mb-10">
            <div className="hidden md:block absolute top-10 left-[14%] right-[14%] h-px" style={{ background: `linear-gradient(to right, transparent, ${F[600]}, transparent)` }} />
            {STEPS.map((item, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <button onClick={() => setActiveStep(i)} className="text-center group w-full">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-black mx-auto mb-4 transition-all duration-300 border-2"
                    style={activeStep === i
                      ? { background: F[500], color: F.c50, borderColor: F[500], transform: "scale(1.1)", boxShadow: `0 8px 24px ${F[500]}40` }
                      : { background: F[700], color: F.m400, borderColor: F[600] }}
                    onMouseEnter={(e) => activeStep !== i && (e.currentTarget.style.borderColor = F[400])}
                    onMouseLeave={(e) => activeStep !== i && (e.currentTarget.style.borderColor = F[600])}>
                    {item.step}
                  </div>
                  <h3 className="font-bold text-sm transition-colors" style={{ color: activeStep === i ? F[300] : F.c100 }}>{item.title}</h3>
                </button>
              </Reveal>
            ))}
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={activeStep} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }}
              className="rounded-2xl border p-8 flex items-start gap-4" style={{ background: F[700], borderColor: F[600] }}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-black text-lg flex-shrink-0"
                style={{ background: F[500] }}>
                {STEPS[activeStep].step}
              </div>
              <div>
                <h4 className="font-bold text-lg mb-2" style={{ color: F.c100 }}>{STEPS[activeStep].title}</h4>
                <p className="leading-relaxed" style={{ color: F.m300 }}>{STEPS[activeStep].detail}</p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ── PRICING (warm cream) ─────────────────────────────────── */}
      <section id="pricing" className="py-24" style={{ background: F.c100 }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <Reveal className="text-center mb-14">
            <SectionLabel text="Pricing" color={F[500]} />
            <h2 className="text-4xl sm:text-5xl font-black mb-4" style={{ fontFamily: "Chivo, sans-serif", color: F[900] }}>
              Simple, Honest Pricing
            </h2>
            <p className="text-lg" style={{ color: F.m600 }}>Free for everyone during our launch month. No hidden costs.</p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Free */}
            <Reveal delay={0.1}>
              <div className="relative rounded-2xl border-2 p-8" style={{ borderColor: F[500], background: F[900] }}>
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="text-xs font-bold px-4 py-1 rounded-full shadow-lg" style={{ background: F[500], color: F.c50 }}>CURRENT PLAN</span>
                </div>
                <p className="text-sm mb-1" style={{ color: F.m400 }}>Free</p>
                <p className="text-5xl font-black mb-1" style={{ color: F.c100 }}>₹0 <span className="text-lg font-normal" style={{ color: F.m500 }}>/ month</span></p>
                <p className="text-sm mb-6 font-medium" style={{ color: F[300] }}>Free forever during launch · No credit card</p>
                <ul className="space-y-3 mb-8">
                  {["Unlimited projects","AI site classification","Auto layout generation","CAPEX cost modeling","ROI & payback analysis","India compliance checklist","Government subsidy finder","Hardware recommendations","PDF report export"].map((f, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <Check className="w-4 h-4 flex-shrink-0" style={{ color: F[300] }} />
                      <span className="text-sm" style={{ color: F.m300 }}>{f}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={handleCTA} className="w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all hover:opacity-90"
                  style={{ background: F[500], color: F.c50 }}>
                  Get Started Free <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </Reveal>
            {/* Pro */}
            <Reveal delay={0.2}>
              <div className="relative rounded-2xl border-2 p-8 opacity-65" style={{ borderColor: F[200], background: "#fff" }}>
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="text-xs font-bold px-4 py-1 rounded-full" style={{ background: F.c200, color: F.m600 }}>COMING Q3 2026</span>
                </div>
                <p className="text-sm mb-1" style={{ color: F.m600 }}>Pro</p>
                <p className="text-5xl font-black mb-1" style={{ color: F[900] }}>TBD</p>
                <p className="text-sm mb-6" style={{ color: F.m600 }}>Pricing based on your feedback</p>
                <ul className="space-y-3 mb-8">
                  {["Everything in Free","White-label PDF reports","Team collaboration","Priority AI processing","API access","Custom branding","Priority support","Advanced scenario modelling"].map((f, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <Star className="w-4 h-4 flex-shrink-0" style={{ color: F.e400 }} />
                      <span className="text-sm" style={{ color: F.m600 }}>{f}</span>
                    </li>
                  ))}
                </ul>
                <button disabled className="w-full py-3 rounded-xl text-sm border cursor-not-allowed" style={{ borderColor: F[200], color: F.m500 }}>
                  Notify Me When Available
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA (dark forest) ──────────────────────────────── */}
      <section className="py-28 relative overflow-hidden" style={{ background: F[900] }}>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full blur-[100px]" style={{ background: `${F[500]}0e` }} />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Reveal>
            <SectionLabel text="Get Started Today" color={F[300]} />
            <h2 className="text-5xl sm:text-6xl font-black mb-6" style={{ fontFamily: "Chivo, sans-serif", color: F.c100 }}>
              India's EV Boom Is Here.<br />
              <span style={{ color: F[300] }}>Are You Ready?</span>
            </h2>
            <p className="text-lg mb-10 max-w-2xl mx-auto" style={{ color: F.m400 }}>
              Join landowners, developers, EPC contractors, and consultants already planning India's charging future with EV Hub Studio.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => navigate("/try")}
                className="px-10 py-4 rounded-xl font-semibold text-lg flex items-center gap-2 justify-center transition-all hover:opacity-90 shadow-2xl"
                style={{ background: F.e500, color: F.c50, boxShadow: `0 12px 40px ${F.e500}30` }}>
                Try Designer Free <ArrowRight className="w-5 h-5" />
              </button>
              <button onClick={handleCTA}
                className="px-10 py-4 rounded-xl font-semibold text-lg border flex items-center gap-2 justify-center transition-all hover:bg-white/5"
                style={{ borderColor: F[600], color: F.m300 }}>
                Sign In with Google
              </button>
            </div>
            <p className="text-sm mt-5" style={{ color: F.m600 }}>No credit card · No expiry during launch · 29 states covered</p>
          </Reveal>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────── */}
      <footer className="py-14" style={{ background: F[950], borderTop: `1px solid ${F[800]}` }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-10 mb-10">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: F[500] }}>
                  <Leaf className="w-5 h-5" style={{ color: F.c50 }} />
                </div>
                <span className="font-black" style={{ fontFamily: "Chivo, sans-serif", color: F.c100 }}>EV Hub Studio</span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: F.m600 }}>AI-powered EV charging station planning for India's infrastructure developers.</p>
            </div>
            <div>
              <p className="font-semibold text-sm mb-4" style={{ color: F.c100 }}>Platform</p>
              <ul className="space-y-2">
                {[
                  { label: "Planner", href: "/planner" },
                  { label: "ROI Calculator", href: "/roi-calculator" },
                  { label: "Vendors", href: "/vendors" },
                  { label: "Site Selection", href: "/site-selection" },
                  { label: "Charger Catalog", href: "/charger-catalog" },
                  { label: "Blog", href: "/blog" },
                  { label: "Contact", href: "/contact" },
                ].map((link) => (
                  <li key={link.label}><a href={link.href} className="text-sm transition-colors hover:text-forest-300" style={{ color: F.m600 }}>{link.label}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-semibold text-sm mb-4" style={{ color: F.c100 }}>Company</p>
              <div className="flex items-center gap-3">
                <img src="/stuthi-logo.png" alt="Stuthi Energy" className="h-8 w-8 rounded-full object-cover" />
                <div>
                  <p className="text-sm font-medium" style={{ color: F.m300 }}>Stuthi Energy Private Limited</p>
                  <p className="text-xs" style={{ color: F.m600 }}>EV Infrastructure Planning</p>
                </div>
              </div>
            </div>
          </div>
          <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-3" style={{ borderTop: `1px solid ${F[800]}` }}>
            <div className="flex flex-wrap items-center gap-4">
              <p className="text-sm" style={{ color: F.m600 }}>© {new Date().getFullYear()} Stuthi Energy Private Limited. All rights reserved.</p>
              <a href="/privacy" className="text-sm hover:underline" style={{ color: F.m600 }}>Privacy Policy</a>
              <a href="/terms" className="text-sm hover:underline" style={{ color: F.m600 }}>Terms of Service</a>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded flex items-center justify-center" style={{ background: `${F[500]}25` }}>
                <Leaf className="w-3 h-3" style={{ color: F[400] }} />
              </div>
              <span className="text-xs" style={{ color: F.m600 }}>Powered by Claude AI</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
